import {CombinedTimelineDayComponentConfig, StatusUpdatedEvent, WorkingTimeTimelineDayContent} from "../timelineTypes";
import {
	EVENT_WORKING_TIME_DELETED,
	EVENT_WORKING_TIME_DUPLICATE,
	EVENT_WORKING_TIME_EDIT,
	EVENT_WORKING_TIME_SHOW,
	EVENT_WORKING_TIME_STATUS_UPDATED
} from "../timelineConstants";
import {htmlEscape} from "@/shared/utils/string";
import {formatDuration, formatDurationDays} from "@/shared/utils/datefunctions";
import {toRecordedEntryStatus} from "@/shared/utils/recordMapper";
import {TimelineContextMenu} from "../timelineContextMenu";
import {
	buildContextMenu,
	buildValidationsTable,
	deleteWorkingTime,
	isWorkingTimeDeletable,
	isWorkingTimeDuplicateable,
	isWorkingTimeEditable,
	isWorkingTimeStatusNumberEnabled,
	setWorkingTimeStatus
} from "../timelineHelper";
import {showWorkingTimeBulkResult} from "@/shared/bulkOperationHelper";
import {isValidationRelevant, shouldAddStatusNumber} from "@/shared/securityHelper";
import {ValidationResult, WorkingTime, WorkingTimeBulkOperationResult} from "@/shared/timr";

/**
 * Component to display day based working times in a timeline day.
 * This component is used inside the <code>TimelineDay</code> to render day based working times for a specific day. If available, this component is appended to the day container.
 *
 * For an overview of the timeline architecture, have a look into the UML diagram "timeline_web_components.png"
 */
export class CombinedTimelineDayBasedWorkingTimes extends HTMLElement {
	private _timelineDay: WorkingTimeTimelineDayContent;
	private _config: CombinedTimelineDayComponentConfig;

	set timelineDay(value: WorkingTimeTimelineDayContent) {
		this._timelineDay = value;
	}

	set config(value: CombinedTimelineDayComponentConfig) {
		this._config = value;
	}

	get config(): CombinedTimelineDayComponentConfig {
		return this._config;
	}

	/**
	 * Main entry point to render the day based working times.
	 */
	public connectedCallback(): void {
		// timeline day and day based working times are required to render this component
		if (!this._timelineDay || !this._timelineDay.dayBasedWorkingTimes.length) {
			return;
		}
		const items = this._timelineDay.dayBasedWorkingTimes
			.map(workingTime => {
				const escapedWorkingTimeTypeName = htmlEscape(workingTime?.workingTimeType.name);
				const escapedWorkingTimeDescription = htmlEscape(workingTime.description);

				const div = document.createElement("div");
				div.dataset.toggle = "context";
				div.classList.add("day-based-working-time");
				div.innerHTML = `
					<!-- timestamps and circles -->
					<span class="times">
						<span>${formatDurationDays(workingTime.durationDays)} ${messages["report.unit.day"]}</span>
						<span></span>
					</span>
					<div class="panel panel-day-based-working-time border-color border-${workingTime.workingTimeType.category} pb-15">
						<!-- working time -->
						<div class="panel-heading">
							<div>
								<div class="item-working-time-type">${escapedWorkingTimeTypeName}</div>
								<div class="duration-container">
									${formatDuration(workingTime.durationNet)}
									<div class="info-icons-container">
										<i class='far fa-status-${toRecordedEntryStatus(workingTime.status)}'></i>
									</div>
								</div>
								${buildContextMenu().outerHTML}
							</div>
							<div class="description-container" style="${!escapedWorkingTimeDescription ? "display: none" : ""} ">
								<i class="fal fa-comment-alt position-left"></i>
								<span data-description class="description-clamped">${escapedWorkingTimeDescription}</span>
							</div>
						</div>

						<!-- validationResults -->
						${buildValidationsTable(this._timelineDay.validationResults.filter(v => isValidationRelevant(v)), workingTime.id).outerHTML}
					</div>`;

				this.buildContextMenuForItem(div, workingTime);

				// clicking the working time item (except dropdown menu) opens the appropriate dialog
				div.addEventListener("click", (e) => {
					if (e.target instanceof HTMLElement && !e.target.closest(".dropdown-menu")) {
						this.dispatchWorkingTimeEvent(EVENT_WORKING_TIME_SHOW, workingTime);
					}
				});
				return div;
			});
		this.append(...items);
	}

	private buildContextMenuForItem(div: HTMLElement, workingTime: WorkingTime): void {
		const workingTimeStatusNumber = toRecordedEntryStatus(workingTime.status);

		const contextMenu = new TimelineContextMenu<WorkingTime>()
			.addMenuEntry({
				label: "timeline.open",
				action: wt => this.dispatchWorkingTimeEvent(EVENT_WORKING_TIME_SHOW, wt)
			})
			.addMenuEntry({
				label: "timeline.edit",
				active: wt => isWorkingTimeEditable(wt),
				action: wt => this.dispatchWorkingTimeEvent(EVENT_WORKING_TIME_EDIT, wt)
			})
			.addMenuEntry({
				label: "report.duplicate",
				active: wt => isWorkingTimeDuplicateable(wt),
				action: wt => this.dispatchWorkingTimeEvent(EVENT_WORKING_TIME_DUPLICATE, wt)
			})
			.addMenuEntry({
				label: "report.delete",
				active: wt => isWorkingTimeDeletable(wt),
				action: wt => {
					deleteWorkingTime(wt)
						?.done(() => this.dispatchEvent(new CustomEvent(EVENT_WORKING_TIME_DELETED, {bubbles: true})));
				}
			});

		if (this._config.workingTimeStatusNumbers.some(statusNumber => shouldAddStatusNumber(statusNumber, workingTimeStatusNumber, false))) {
			contextMenu.addDivider();
		}

		for (const statusNumber of this._config.workingTimeStatusNumbers) {
			const statusName = messages["reports.filter.status." + statusNumber];
			const title = messages.withArguments("report.setStatus", statusName);
			contextMenu.addMenuEntry({
				label: title,
				show: () => shouldAddStatusNumber(statusNumber, workingTimeStatusNumber, false),
				action: wt => {
					setWorkingTimeStatus(wt, statusNumber)
						.done((result: WorkingTimeBulkOperationResult) => {
							if (!result.allUpdated) {
								showWorkingTimeBulkResult(result, "report.setStatus.");
							}
							if (result.updated > 0) {
								this.dispatchEvent(new CustomEvent<StatusUpdatedEvent>(
									EVENT_WORKING_TIME_STATUS_UPDATED, {
										detail: {statusName},
										bubbles: true
									}
								));
							}
						});
				},
				active: wt => isWorkingTimeStatusNumberEnabled(wt, statusNumber)
			});
		}
		contextMenu.bindToElement(div, workingTime);
	}

	private getValidationResults(workingTime: WorkingTime): ValidationResult[] {
		return this._timelineDay.validationResults
			.filter(validation => validation.recordedEntry?.id === workingTime.id || validation.recordedEntryRef?.id === workingTime.id);
	}

	/**
	 * Dispatches a working time item event (e.g. show, edit, duplicate) which subsequently opens a dialog outside this component
	 */
	private dispatchWorkingTimeEvent(eventName: string, workingTime: WorkingTime): void {
		this.dispatchEvent(new CustomEvent(eventName, {
			detail: {workingTimeItem: {workingTime, validationResults: this.getValidationResults(workingTime)}},
			bubbles: true
		}));
	}
}
